import { pageConfig } from '@/common/service/page-config';
import store from '@/store/index';
import { addAddonPlans } from '@/apis';
import { pollingEventStatus } from '@/common/polling';
import { upgradeSuccessPage, wiseUpgradeSuccessPage } from '@/common/service/common';
import {
  updatePlansCheckout,
  addParamsIfApm,
  addParamsIfReferralProgram,
  getUnsupportBillingCycleCategoryMap,
  addParamsIfNeedUseVerification,
  UpdateTypes,
  setMultipleRatePlanFlag,
  addPlanTypes,
  getPlanCodeMap
} from '@/common/service/checkout-utils';
import { de$type, getAudioCountriesParams, getTargetUserIdParam } from '@/common/service/flow/utils';
import { preCheckForPbx } from '@/common/service/validate/pbx';
import _, { isArray } from 'lodash';
import { getPlansMap } from '@/common/product';
import {
  planTypes,
  ABANDON_CART_EMAIL_COOKIE_KEY,
  BIZ_PLUS_PLAN_CODES_ONLINE_SUPPORTED,
  APM_PAYMENT_METHODS,
} from '@/common/constants';
import { PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import { validAddonCycleForBase } from '@/common/service/validate/billing-cycle';
import { showCouponTermsMessage } from '@/views/config/coupon/helper/utils';
import { WISE } from '@/views/checkout/payment/helper/constant';
import { checkBizPlusLicensePromise } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { isAutoAddableAndAutoAddPlanSelected } from '@/plans/ZOOM_MEETING/common/utils';
import { plansof } from '@/utils/chain';
import { checkCountriesRegionsOption } from '@/common/service/validate/tollfree';
import { getPartnershipParams } from '@/views/config/partner-refer/helper/params';
import { checkZoneEduPlusLicense } from '@/plans/ZONE_EDU_PLUS/utils';
import { getOrderSummaryFields } from '../../utils';
import { preCheckZoneProPlusForAdd } from '@/plans/ZONE_PRO_PLUS/utils/validator';
import { $cookie } from '@/common/utils'
import { injectDevicesParams, preSubmitForVendorProduct } from '@/flows/helper/vendor-product';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';

const addType = pageConfig?.editConfig?.planType;

const getBizPlusUpsellForm = (
  getters,
  supported = BIZ_PLUS_PLAN_CODES_ONLINE_SUPPORTED,
  types = [planTypes.ZOOM_ONE_BUNDLE],
  actionType = UpdateTypes.BIZ_PLUS
) => {
  const _getPlanCodeMap = getPlanCodeMap(getters, types);
  const boughtPlansMap = _getPlanCodeMap(getters.boughtPlans.filter(p => supported.includes(p.plan_code)));
  const selectedPlansMap = _getPlanCodeMap(getters.getSelectedPlans.filter(p => !p.isVendorProduct));
  const unSupportBillingCycleMap = getUnsupportBillingCycleCategoryMap(boughtPlansMap, selectedPlansMap)
  let planList = [];

  Object.keys(selectedPlansMap).forEach((planCode) => {
      const [quantity, category, planType] = selectedPlansMap[planCode];
      const [quantity1] = boughtPlansMap?.[planCode] ?? [];

      const extraSubmitKeys = {}
      if (unSupportBillingCycleMap[category]) {
        if (quantity1) {
          Object.assign(extraSubmitKeys, { planType, oldPlanType: planType })
        } else {
          // Only need planType field when add new plan
          Object.assign(extraSubmitKeys, { planType })
        }
      }

      // Just quantity changed
      if (quantity1) {
        planList.push({
          oldPlanCode: planCode,
          planCode,
          quantity,
          category,
          ...extraSubmitKeys
        });
      } else {
        // Add new plan
        planList.push({
            planCode,
            quantity,
            category,
            ...extraSubmitKeys
        });
      }
  });

  planList.forEach(p => {
    Reflect.deleteProperty(p, 'category');
  });

  const isBulkLicenseAccount = getters.basicConfig?.isBulkLicenseAccount;
  if (isBulkLicenseAccount) {
    planList = planList.filter(p => p.quantity !== 0);
  }

  return { planList, actionType };
};

function getProPlusUpsellForm(getters) {
  const getPlanInfoByCode = planCode => getters.planMapFlat.find(item => item.plan_code === planCode);
  const proPlusEditPlansFilter = x => (
    de$type(x.type) === planTypes.ZONE_PRO_PLUS ||
    (de$type(x.type) === planTypes.ZOOM_PHONE && [PBX_SUB_TYPES.TOLLFREE, PBX_SUB_TYPES.PHONE_NUMBER].includes(x.sub_type) && x.region === PBX_REGIONS.US_AND_CANADA) ||
    (de$type(x.type) === planTypes.ZOOM_PHONE && PBX_SUB_TYPES.BASE === x.sub_type)
  );
  const boughtPlans = (getters.boughtPlans ?? []).filter(x => (getters.basicConfig.isExpireAccountWithPaidPlan ? x.cancelled != 1 : true));
  const changedPlans = (getters.selectedPlanListPayLoad ?? []).filter(item => {
    const p = getPlanInfoByCode(item.planCode);
    if (!p) return true;
    return proPlusEditPlansFilter(p);
  });
  const payload = changedPlans.map(item => {
    const oldPlan = boughtPlans.find(p => p.plan_code === item.planCode)
    if (oldPlan?.sub_type === PBX_SUB_TYPES.BASE) {
      return null;
    }
    if (oldPlan) {
      return {
        ...item,
        oldPlanCode: item.planCode,
      };
    }
    return item;
  }).filter(Boolean);

  return {
    planList: addPlanTypes(getters, payload),
    actionType: UpdateTypes.UPDATE_PRO_PLUS,
  };
}

function getExtensionData() {
  const { campaign_code: campaignCode = '', trial_code: trialCode = '', mid } = pageConfig || {};
  const audioCountriesParams = getAudioCountriesParams(store);
  const partnerReferParams = getPartnershipParams();
  const targetUserIdParams = getTargetUserIdParam();

  return {
    ...(mid ? { meetingId: mid } : {}),
    ...(!audioCountriesParams ? {} : { tollFreeProfileData: audioCountriesParams }),
    ...partnerReferParams,
    ...(campaignCode ? { campaignCode } : {}),
    ...(trialCode ? { trialCode } : {}),
    scheduledEmailId: $cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY),
    ...targetUserIdParams
  }
}

const getBeforeParamsForPbx = () => {
  const getters = store.getters;
  const orderDataPayload = getters.orderDataPayload;

  if (getters?.fromCampaignLink) {
    const boughtPlanMap = getPlansMap(getters.boughtPlans);

    return {
      ...orderDataPayload,
      planList: _.filter(orderDataPayload?.planList, (item) => !_.has(boughtPlanMap, item.planCode)),
    };
  }

  return orderDataPayload;
};

const filterPbxBaseWhenHasPurchased = (planList = []) => {
  const getters = store.getters;
  const pbxBasePlan = getters.boughtPlans.find((p) => p.sub_type === PBX_SUB_TYPES.BASE);

  if (pbxBasePlan?.plan_code) {
    return planList.filter((p) => p.planCode !== pbxBasePlan.plan_code);
  }

  return planList;
};

/**
 * add update params,
 * allow edit base meeting plan for auto-add in add-to-cart flow
 * @jira https://zoomvideo.atlassian.net/browse/OBT-4372
 */

const changeActionTypeIfNeeded = (params) => {
  if (store.getters.basicConfig.isExpireAccountWithPaidPlan){
    params.actionType = UpdateTypes.EXPIRE_ACCOUNT_WITH_PAID_PLAN;
  }
  return params;
}

const addUpdateParamsIfAutoAdd = (params) => {
  if (!isAutoAddableAndAutoAddPlanSelected()) {
    return;
  }
  const { planList = [] } = params.orderData;
  const updateList = [];
  const addList = [];
  planList.forEach((p) => {
    if (store.getters.getBoughtMeetingPlans.some((boughtPlan) => boughtPlan.plan_code === p.planCode && (store.getters.basicConfig.isExpireAccountWithPaidPlan ? boughtPlan.cancelled != 1 : true))) {
      updateList.push({ ...p, oldPlanCode: p.planCode });
    } else {
      addList.push(p);
    }
  });
  if (updateList.length > 0) {
    params.updateList = updateList;
    params.actionType = UpdateTypes.ADD_TO_CART_UPDATE;
    params.orderData.planList = addList;
  }
};

const checkoutSubmitForProPlus = async (submitParams) => {

  const getters = store.getters;
  const couponData = getters.orderDataPayload.coupon;
  const formData = getProPlusUpsellForm(getters);

  const hasBoughtPlan = !!formData.planList.find(i => !!i.oldPlanCode);

  if (!hasBoughtPlan) return checkOutSubmitForAddNew(submitParams, formData.planList);

  setMultipleRatePlanFlag(formData);
  const partnerReferParams = getPartnershipParams();

  const extensionData = {
    ...partnerReferParams,
    scheduledEmailId: window.$cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY),
  };

  return updatePlansCheckout(
    submitParams,
    formData.planList,
    formData.actionType,
    couponData,
    extensionData,
    formData.multiRatePlan,
    injectDevicesParams
  );
}

function getPaymentInfoData() {
  const getters = store.getters;
  const { paymentResponse, paymentGateway } = getters.paymentMethodInfo;
  return {
    zAccountId: getters.billingInfo.zAccountId,
    paymentResponse,
    paymentGateway,
    creditCardReAuth: false,
  }
}

const checkOutSubmitForAddNew = async (
  submitParams,
  pList = null
) => {
  const getters = store.getters;
  const { apmInfo } = getters;
  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  let { coupon, planList } = store.getters.orderDataPayload;
  if (isArray(pList)) {
    planList = pList;
  }

  planList = filterPbxBaseWhenHasPurchased(planList);

  let params = {
    orderData: {
      coupon,
      planList,
    },
    paymentInfo: getPaymentInfoData(),
    extension: getExtensionData(),
    preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };

  addParamsIfNeedUseVerification(params.extension);

  if (addType === planTypes.ZOOM_PHONE) {
    params.orderData = getBeforeParamsForPbx();
  }

  addParamsIfApm(params);
  addParamsIfReferralProgram(params);
  // add update params for auto add
  // @jira https://zoomvideo.atlassian.net/browse/OBT-4372
  addUpdateParamsIfAutoAdd(params);

  changeActionTypeIfNeeded(params);

  await injectDevicesParams(params);

  const result = await addAddonPlans(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  store.commit('setTransactionId', eventResult?.uniqueKey);
  store.commit('setPreviewKey', eventResult?.previewKey);

  if (!preview) {
    const apmModalSuccessPageNeeded =
      apmInfo?.isApm &&
      [APM_PAYMENT_METHODS.FURIKOMI, APM_PAYMENT_METHODS.P24, APM_PAYMENT_METHODS.KLARNA].includes(
        apmInfo.apmPaymentMethodName,
      );
    apmInfo.apmPaymentMethodName === WISE
      ? wiseUpgradeSuccessPage()
      : upgradeSuccessPage(undefined, undefined, 2000, undefined, apmModalSuccessPageNeeded);
  }

  return {
    eventResult,
  };
};

export const getPbxUpdateForm = getters => {
  const planList = (getters.getSelectedPlans ?? [])
  .filter(x => x.sub_type !== PBX_SUB_TYPES.BASE)
  .filter(x => !x.isVendorProduct)
  .map(x => {
    let quantity = x.cartInfo.selected_number;
    return {
      quantity,
      planCode: x.plan_code,
    };
  });
  return {
    planList,
    actionType: UpdateTypes.UPDATE_PBX,
  };
}

const checkOutSubmitForAddMorePbx = async (submitParams) => {
  const getters = store.getters;
  const updateForm = getPbxUpdateForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  const partnerReferParams = getPartnershipParams();

  const extensionData = {
    ...updateForm.extensionUpdateData,
    ...partnerReferParams,
    scheduledEmailId: window.$cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY),
  };
  return updatePlansCheckout(
    submitParams,
    updateForm.planList,
    updateForm.actionType,
    couponData,
    extensionData,
    updateForm.multiRatePlan,
    injectDevicesParams
  );
}

const checkoutSubmitForAddMoreBizPlus = async (submitParams) => {
  const getters = store.getters;
  const updateForm = getBizPlusUpsellForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  return updatePlansCheckout(
    submitParams,
    updateForm.planList,
    updateForm.actionType,
    couponData,
    updateForm.extensionUpdateData,
    false,
    injectDevicesParams
  );
}

function submitUpsell(submitParams) {
  const isProPlusContext = pageConfig?.editConfig?.planType === planTypes.ZONE_PRO_PLUS;
  const isBizPlusContext = pageConfig?.editConfig?.planType === planTypes.ZOOM_ONE_BUNDLE;
  const isPbxContext = pageConfig?.editConfig?.planType === planTypes.ZOOM_PHONE;

  const hasBundlePlan = plansof(store?.getters?.boughtPlans).type(planTypes.ZOOM_ONE_BUNDLE).has();
  const hasPbxPlan = store.getters.boughtPlans.some(x => x.type === planTypes.ZOOM_PHONE);
  // const hasProPlusPlan = store.getters.getSelectedPlans.some(x => x.type === planTypes.ZOOM_MEETING);

  if (isProPlusContext) {
    return checkoutSubmitForProPlus(submitParams);
  }
  if (isBizPlusContext && hasBundlePlan) {
    return checkoutSubmitForAddMoreBizPlus(submitParams);
  }
  if (isPbxContext && hasPbxPlan) {
    return checkOutSubmitForAddMorePbx(submitParams);
  }
  return checkOutSubmitForAddNew(submitParams);
}


async function beforeSubmitUpsell(needCheckZmHardware) {
  if (store.getters.hasZoomDevice && needCheckZmHardware) {
    await preSubmitForVendorProduct();
  }
  await checkBizPlusLicensePromise();
  await checkZoneEduPlusLicense();
  await preCheckForPbx();
  await preCheckZoneProPlusForAdd();
  await checkCountriesRegionsOption();
  await validAddonCycleForBase();
  await showCouponTermsMessage(store.getters.tncMessage, store.getters);
}

export {
  submitUpsell,
  beforeSubmitUpsell,
}
